import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Smartphone, Phone, Clock, RefreshCw, ChevronDown, ChevronRight, ExternalLink, Play } from 'lucide-react';
import client from '../../api/client';

const CHANNEL_CONFIG = {
  email: { icon: Mail, label: 'Email', emoji: '📧' },
  sms: { icon: Smartphone, label: 'SMS', emoji: '📱' },
  voice: { icon: Phone, label: 'Voice', emoji: '📞' },
};

const STATUS_COLORS = {
  sent: 'bg-yellow-100 text-yellow-700',
  queued: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700',
  answered: 'bg-green-100 text-green-700',
  opened: 'bg-blue-100 text-blue-700',
  clicked: 'bg-blue-100 text-blue-700',
  bounced: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  no_answer: 'bg-gray-100 text-gray-600',
  voicemail: 'bg-gray-100 text-gray-600',
};

function formatRelativeTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatDuration(seconds) {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatAbsoluteTime(ts) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function CommCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const ch = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.email;
  const Icon = ch.icon;
  const statusColor = STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600';

  return (
    <div className="border border-gray-100 rounded-lg hover:border-gray-200 transition">
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.channel === 'email' ? 'bg-blue-50' : item.channel === 'sms' ? 'bg-emerald-50' : 'bg-purple-50'}`}>
          <Icon className={`w-4 h-4 ${item.channel === 'email' ? 'text-blue-600' : item.channel === 'sms' ? 'text-emerald-600' : 'text-purple-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusColor}`}>{item.status}</span>
            <span className="text-xs text-gray-500 truncate">{item.to_address}</span>
          </div>
          {item.subject && <p className="text-sm font-medium text-gray-900 truncate mt-0.5">{item.subject}</p>}
          {!item.subject && item.body_preview && <p className="text-sm text-gray-700 truncate mt-0.5">{item.body_preview}</p>}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-[10px] text-gray-400" title={formatAbsoluteTime(item.created_at)}>
              {formatRelativeTime(item.created_at)}
            </span>
            {item.channel === 'email' && (item.opens_count > 0 || item.clicks_count > 0) && (
              <span className="text-[10px] text-blue-600">
                {item.opens_count > 0 && `Opened ${item.opens_count}×`}
                {item.opens_count > 0 && item.clicks_count > 0 && ' · '}
                {item.clicks_count > 0 && `Clicked ${item.clicks_count}×`}
              </span>
            )}
            {item.channel === 'voice' && item.duration_seconds != null && (
              <span className="text-[10px] text-purple-600">Duration: {formatDuration(item.duration_seconds)}</span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 mt-1">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3 border-t border-gray-50">
          {item.recording_url && (
            <a href={item.recording_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 mb-1 text-xs text-purple-600 hover:text-purple-700">
              <Play className="w-3 h-3" /> Play Recording
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {item.events && item.events.length > 0 ? (
            <div className="mt-2 space-y-1">
              {item.events.map((evt, i) => (
                <div key={evt.id || i} className="flex items-center gap-2 text-[11px]">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                  <span className="font-medium text-gray-700">{evt.event_type}</span>
                  <span className="text-gray-400">{formatAbsoluteTime(evt.occurred_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 mt-2">No delivery events yet</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommunicationTimeline({ recordId }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (channelFilter !== 'all') params.channel = channelFilter;
      if (statusFilter) params.status = statusFilter;

      const { data } = await client.get(`/records/${recordId}/communications`, { params });
      setItems(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 25, total: 0 });
    } catch (err) {
      console.error('Failed to load communications:', err);
    } finally {
      setLoading(false);
    }
  }, [recordId, channelFilter, statusFilter]);

  useEffect(() => { load(1); }, [load]);

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 text-sm">Communications</h3>
          {pagination.total > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{pagination.total}</span>}
        </div>
        <button onClick={() => load(pagination.page)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-50 flex-wrap">
        {['all', 'email', 'sms', 'voice'].map(ch => (
          <button key={ch} onClick={() => setChannelFilter(ch)}
            className={`px-2.5 py-1 text-xs rounded-full font-medium transition ${channelFilter === ch ? 'bg-brand-100 text-brand-700' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
            {ch === 'all' ? 'All' : (CHANNEL_CONFIG[ch]?.emoji || '') + ' ' + (CHANNEL_CONFIG[ch]?.label || ch)}
          </button>
        ))}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="ml-auto text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-500">
          <option value="">Any status</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="p-3 space-y-2">
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No communications yet</p>
            <p className="text-gray-300 text-xs mt-1">Emails, SMS, and calls will appear here</p>
          </div>
        ) : (
          items.map(item => <CommCard key={item.id} item={item} />)
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            Page {pagination.page} of {totalPages} ({pagination.total} total)
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => load(pagination.page - 1)} disabled={pagination.page <= 1}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30">Prev</button>
            <button onClick={() => load(pagination.page + 1)} disabled={pagination.page >= totalPages}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
